import useFetchClient from '@/hooks/useFetchClient';
import useSearch from '@/hooks/useSearch';
import { apiRequest } from '@/services';
import { CustomDataTable } from '@/components/common/ui/table/CustomDataTable';
import { Pagination } from '@/types/pagination';
import { UserColumns } from '@/pages/student-management/table/UserColumns';
import { UserColumns as UserColumnsCoach } from '@/pages/coach-management/table/UserColumns';
import { useTranslation } from '@/config/i18n';
import usePagination from '@/hooks/usePagination';
import { useContext, useMemo, useState } from 'react';
import usePathName from '@/hooks/usePathName';
import PATH from '@/routes/path';
import {
  ROLE,
  STAGE_GRADUATION_STATUS,
  SUBSCRIPTION_PAYMENT_STATUS,
} from '@/shared/constants/user';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/shared/constants/pagination';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '@/components/common/loader/LoadingContext';
import DialogCustom from '@/components/common/ui/dialog';
import ButtonCustom from '@/components/common/ui/button';
import { ToastContext } from '@/components/common/CommonToast';
import { normalizeTimeZone, toastErrorMessage } from '@/lib/utils';
import { HTTP_STATUS, UPDATE_STATUS_USER_ERROR_CODE } from '@/shared/constants';
import { IconCheckboxChecked, IconCheckboxEmpty } from '../icons';

type Props = {
  uploadCsv: string;
  searchTrigger: number;
};

const UserList: React.FC<Props> = ({ uploadCsv, searchTrigger }) => {
  const [t] = useTranslation('');
  const { apiService } = apiRequest();
  const { searchParams } = useSearch();
  const { toast } = useContext(ToastContext);
  const { query } = Object.fromEntries(searchParams.entries());
  const navigate = useNavigate();
  const pathName = usePathName();
  const { setLoadingGlobal } = useLoading();
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogConfirmPaidVisible, setDialogConfirmPaidVisible] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState<any>();
  const [confirmPaid, setConfirmPaid] = useState(false);
  const [changeStatusSuccess, setChangeStatusSuccess] = useState(false);

  const role = useMemo(() => {
    return pathName.includes(PATH.student_management)
      ? ROLE.STUDENT
      : ROLE.TEACHER;
  }, []);

  const params = useMemo(() => {
    return query
      ? undefined
      : { limit: DEFAULT_PER_PAGE, paginate: DEFAULT_PAGE, role };
  }, [query]);

  const queryCustom = useMemo(() => {
    return query
      ? `${query}&role=${role}&timezone=${normalizeTimeZone(
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      )}`
      : `timezone=${normalizeTimeZone(
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      )}`;
  }, [query, role]);

  const { data: users, error } = useFetchClient(
    async () => await apiService.users.query(queryCustom, params),
    [query, uploadCsv, changeStatusSuccess, searchTrigger],
    {
      isLoadingGlobal: true,
      isScrollToTop: true,
    },
  );

  if (error) {
    toast?.current?.show(toastErrorMessage(t('common.toast.fail')));
  }

  const { paginationComputed, setPagination } = usePagination(users?.pagination);

  function handleEditUser(id: number) {
    if (role === ROLE.TEACHER) {
      navigate(`${PATH.coach_management}/${PATH.coach_management_edit}/${id}`);
      return;
    }
    navigate(
      `${PATH.student_management}/${PATH.student_management_edit}/${id}`,
    );
  }

  const handleError = (error: any) => {
    const { status_code, errors, error_code } = error;
    if (status_code === HTTP_STATUS.FORM_ERROR) {
      const errorLimitStudent = errors?.find(
        (e: any) => e.error_code === 'MFS-E-LEARNING-ADMIN-USER-169',
      );
      if (errorLimitStudent) {
        toast?.current?.show(toastErrorMessage(errorLimitStudent?.message));
      }
    }

    if (
      error_code === UPDATE_STATUS_USER_ERROR_CODE.RESUME_SUBSCRIPTION_ERROR
    ) {
      toast?.current?.show(
        toastErrorMessage(
          t('user_list.change_status_error.resume_subscription_error'),
        ),
      );
    }

    if (error_code === UPDATE_STATUS_USER_ERROR_CODE.ACTIVE_CHANGE_FAILED) {
      toast?.current?.show(
        toastErrorMessage(
          t('user_list.change_status_error.active_change_failed'),
        ),
      );
    }

    if (error_code === UPDATE_STATUS_USER_ERROR_CODE.TIMEOUT) {
      toast?.current?.show(
        toastErrorMessage(t('user_list.change_status_error.timeout')),
      );
    }

    if (
      error_code ===
      UPDATE_STATUS_USER_ERROR_CODE.INACTIVE_HAVE_PLAN_OR_GRADUATION_INVALID
    ) {
      toast?.current?.show(
        toastErrorMessage(
          t(
            'user_list.change_status_error.inactive_have_plan_or_graduation_invalid',
          ),
        ),
      );
    }

    if (error_code === UPDATE_STATUS_USER_ERROR_CODE.NOT_FOUND_SUBSCRIPTION) {
      toast?.current?.show(
        toastErrorMessage(
          t('user_list.change_status_error.not_found_subscription'),
        ),
      );
    }

    if (
      error_code === UPDATE_STATUS_USER_ERROR_CODE.NOT_FOUND_SUBSCRIPTION_TOKEN
    ) {
      toast?.current?.show(
        toastErrorMessage(
          t('user_list.change_status_error.not_found_subscription'),
        ),
      );
    }

    if (
      error_code ===
      UPDATE_STATUS_USER_ERROR_CODE.INACTIVE_HAVE_PLAN_OR_GRADUATION_INVALID_TOKEN
    ) {
      toast?.current?.show(
        toastErrorMessage(
          t(
            'user_list.change_status_error.inactive_have_plan_or_graduation_invalid',
          ),
        ),
      );
    }

    if (
      error_code ===
      UPDATE_STATUS_USER_ERROR_CODE.RESUME_SUBSCRIPTION_ERROR_TOKEN
    ) {
      toast?.current?.show(
        toastErrorMessage(
          t('user_list.change_status_error.active_change_failed'),
        ),
      );
    }
  };

  async function handleEditStatusUser(isActive: boolean, rowData: any) {
    setChangeStatusSuccess(false);
    setSelectedUser({ isActive, rowData });
    const { stage_graduation_status, subscription_payment_status } = rowData;
    const isPaid =
      stage_graduation_status === STAGE_GRADUATION_STATUS.PAID &&
      subscription_payment_status === SUBSCRIPTION_PAYMENT_STATUS.SUSPENDED;
    let nextDialogVisibleValue = !dialogVisible;
    if (isPaid && isActive) {
      nextDialogVisibleValue = !dialogConfirmPaidVisible;
      setDialogConfirmPaidVisible(nextDialogVisibleValue);
    } else if (
      subscription_payment_status === SUBSCRIPTION_PAYMENT_STATUS.CANCELED &&
      isActive
    ) {
      toast?.current?.show(
        toastErrorMessage(t('user_list.toast.cannot_active_toggle')),
      );
    } else {
      nextDialogVisibleValue = !dialogVisible;
      setDialogVisible(nextDialogVisibleValue);
    }
    if (nextDialogVisibleValue) return;
    setLoadingGlobal(true);
    const { error: userError } = await apiService.users.update(rowData.id, {
      role,
      should_charge_immediately: confirmPaid,
      status: isActive ? 'active' : 'inactive',
      require_password_update: false,
    });
    setLoadingGlobal(false);
    setConfirmPaid(false);
    if (userError) {
      handleError(userError);
      return;
    }
    setChangeStatusSuccess(true);
  }

  function onSortChanged(paginationState: Pagination) {
    setPagination(paginationState);
  }

  function onPaginationChanged(paginationState: Pagination) {
    setPagination({
      ...paginationComputed,
      paginate: paginationState.paginate,
      limit: paginationState.limit,
    });
  }

  return (
    <>
      <CustomDataTable
        wrapClassName="mt-[10px]"
        values={users?.data ?? []}
        columns={
          role === ROLE.STUDENT
            ? UserColumns(t, handleEditUser, handleEditStatusUser)
            : UserColumnsCoach(t, handleEditUser, handleEditStatusUser)
        }
        pagination={paginationComputed}
        onPaginationChanged={onPaginationChanged}
        onSortChanged={onSortChanged}
      />

      <DialogCustom
        header={t('common.dialog.header')}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        footer={
          <div className="field">
            <div className="flex justify-end">
              <ButtonCustom
                type="button"
                className="cancel-button p-button-sm mr-2"
                severity="secondary"
                onClick={() => setDialogVisible(false)}
              >
                {t('common.dialog.cancel')}
              </ButtonCustom>
              <ButtonCustom
                type="button"
                onClick={() =>
                  handleEditStatusUser(
                    selectedUser.isActive,
                    selectedUser.rowData,
                  )
                }
              >
                {t('common.dialog.ok')}
              </ButtonCustom>
            </div>
          </div>
        }
      >
        {selectedUser?.isActive
          ? t('user_list.message_on')
          : t('user_list.message_off')}
      </DialogCustom>

      <DialogCustom
        header={t('user_list.popup.title')}
        visible={dialogConfirmPaidVisible}
        onHide={() => {
          setDialogConfirmPaidVisible(false);
          setConfirmPaid(false);
        }}
        className="w-[400px]"
        footer={
          <div className="field">
            <div className="flex justify-end">
              <ButtonCustom
                type="button"
                className="cancel-button p-button-sm mr-2"
                severity="secondary"
                onClick={() => {
                  setDialogConfirmPaidVisible(false);
                  setConfirmPaid(false);
                }}
              >
                {t('user_list.popup.btn_cancel')}
              </ButtonCustom>
              <ButtonCustom
                type="button"
                onClick={() =>
                  handleEditStatusUser(
                    selectedUser.isActive,
                    selectedUser.rowData,
                  )
                }
              >
                {t('user_list.popup.btn_ok')}
              </ButtonCustom>
            </div>
          </div>
        }
      >
        <div>{t('user_list.popup.content')}</div>
        <div className="flex items-center mt-3 whitespace-pre-wrap">
          <div
            className="mr-[10px] cursor-pointer"
            onClick={() => setConfirmPaid(!confirmPaid)}
          >
            {confirmPaid ? <IconCheckboxChecked /> : <IconCheckboxEmpty />}
          </div>
          {t('user_list.popup.checkbox_content')}
        </div>
      </DialogCustom>
    </>
  );
};

export default UserList;
